import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  Typography,
  Divider,
  Tooltip,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { MoreVert as MoreIcon, DragIndicator as DragIcon } from "@mui/icons-material";
import CustomButton from "../../../../components/common/button/CustomButton";

interface AreaRule {
  id: number;
  areas: string[];
  assignTo: string;
}

const USERS = ["User A", "User B", "User C", "User D"];

const POSTCODE_OPTIONS = [
  "CO1", "CO2", "CO3", "CO4", "CO5",
  "IG1", "IG2", "IG3", "IG4", "IG5",
  "RM1", "RM2", "RM3", "RM4",
  "E1",  "E2",  "E3",  "N1",  "N2",
  "SW1", "SW2", "W1",  "W2",  "EC1",
];

const AreaAllocation = () => {
  const [rules, setRules] = useState<AreaRule[]>([
    { id: 1, areas: ["CO1", "CO2", "CO3"], assignTo: "User A" },
    { id: 2, areas: ["IG"],                assignTo: "User B" },
    { id: 3, areas: ["RM"],                assignTo: "User C" },
  ]);

  const handleChange = (index: number, field: string, value: any) => {
    setRules((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleDelete = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    setRules((prev) => [...prev, { id: Date.now(), areas: [], assignTo: "User A" }]);
  };

  return (
    <Paper sx={{ padding: "16px", borderRadius: "8px" }}>
      {/* Header */}
      <Box sx={{ marginBottom: "12px" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#333", fontSize: "14px", marginBottom: "4px" }}>
          Area / Postcode-Based Allocation
        </Typography>
        <Typography variant="caption" sx={{ color: "#666", fontSize: "12px" }}>
          Assign leads based on assigned area or postcode.
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer sx={{ border: "1px solid #e0e0e0", borderRadius: "6px", marginBottom: "16px" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600, color: "#333", fontSize: "12px", padding: "6px 8px", width: "55%" }}>
                Area / Postcode
              </TableCell>
              <TableCell sx={{ fontWeight: 600, color: "#333", fontSize: "12px", padding: "6px 8px" }}>
                Assign To
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 600, color: "#333", fontSize: "12px", padding: "6px 8px" }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rules.map((rule, i) => (
              <TableRow key={rule.id} sx={{ "&:hover": { backgroundColor: "#fafafa" } }}>
                {/* Drag handle + Postcode chips */}
                <TableCell sx={{ padding: "4px 8px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <DragIcon sx={{ fontSize: "16px", color: "#bbb", cursor: "grab", flexShrink: 0 }} />
                    <Autocomplete
                      multiple
                      freeSolo
                      options={POSTCODE_OPTIONS}
                      value={rule.areas}
                      onChange={(_, newVal) => handleChange(i, "areas", newVal)}
                      size="small"
                      sx={{ flex: 1 }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            size="small"
                            {...getTagProps({ index })}
                            sx={{ fontSize: "11px", height: "20px" }}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={rule.areas.length === 0 ? "Add postcodes…" : ""}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              fontSize: "12px",
                              minHeight: "30px",
                              padding: "2px 6px",
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </TableCell>

                {/* Assign To */}
                <TableCell sx={{ padding: "4px 8px" }}>
                  <Select
                    value={rule.assignTo}
                    onChange={(e) => handleChange(i, "assignTo", e.target.value)}
                    size="small"
                    sx={{ minWidth: "110px", fontSize: "12px", height: "28px" }}
                  >
                    {USERS.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </Select>
                </TableCell>

                {/* Action */}
                <TableCell align="center" sx={{ padding: "4px 8px" }}>
                  <Tooltip title="Delete rule">
                    <IconButton size="small" onClick={() => handleDelete(i)} sx={{ padding: "2px" }}>
                      <MoreIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomButton title="+ Add Rule" onClick={handleAdd} />

      {/* Hint */}
      <Typography variant="caption" sx={{ display: "block", color: "#999", fontSize: "11px", marginTop: "10px", marginBottom: "10px" }}>
        Area/Postcode rules are checked after Bedroom rules and before Lead Type and Percentage fallback rules.
      </Typography>

      <Divider sx={{ marginBottom: "12px" }} />

      {/* Footer */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Box sx={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: "#02906f" }} />
          <Typography variant="caption" sx={{ color: "#02906f", fontWeight: 500, fontSize: "13px" }}>
            Rules Valid - All set for now!
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <CustomButton title="Reset Draft" variant="outlined" onClick={() => {}} />
          <CustomButton title="Save" onClick={() => {}} />
          <CustomButton title="Save & Activate" onClick={() => {}} />
        </Box>
      </Box>
    </Paper>
  );
};

export default AreaAllocation;